import { Radio, RadioGroup, Stack } from '@chakra-ui/react';
import { PDFViewer } from '@react-pdf/renderer';
import { faker } from '@faker-js/faker';
import { useState } from 'react';
import { capitalize } from 'lodash-es';

import { useBreakpointValue, useTopicGroupsAndSummaries } from '@util/hooks';
import InsightPDF from '@components/pdf/insight-pdf.component';
import MeetingPDF from '@components/pdf/meeting-pdf.component';

const FAKE_INSIGHT = {
  id: faker.string.uuid(),
  headline: faker.lorem.sentence(),
  subheadline: faker.lorem.sentences(),
  sections: [...new Array(faker.number.int({ min: 1, max: 20 })).keys()].map(() => ({
    type: faker.helpers.arrayElement(['intro', 'main_content', 'conclusion']),
    title: faker.lorem.sentence(),
    content: generateContentWithSources()
  })),
  cards: [...new Array(faker.number.int({ min: 2, max: 30 })).keys()].map(() => ({
    id: faker.string.uuid(),
    title: faker.string.alpha({ length: { min: 0, max: 40 } })
  })),
  initiative: {
    id: faker.string.uuid(),
    title: faker.string.alpha({ length: { min: 0, max: 20 } }),
    type: faker.helpers.arrayElement(['Focus', 'Label', 'Lens'])
  },
  cover_image_url: faker.image.url(),
  tenant: {
    id: faker.string.uuid(),
    name: faker.company.name()
  },
  person: {
    id: faker.string.uuid(),
    name: faker.person.fullName()
  },
  shares: [...new Array(faker.number.int({ min: 1, max: 5 })).keys()].map(() => ({
    email: faker.internet.email(),
    created_at: faker.date.past()
  })),
  preferred: faker.datatype.boolean(),
  read_at: faker.date.past().getTime(),
  archived_at: faker.date.past().getTime(),
  created_at: faker.date.past().getTime(),
  updated_at: faker.date.past().getTime()
};

const FAKE_INITIATIVE = {
  id: faker.string.uuid(),
  title: faker.lorem.sentence(),
  description: faker.string.alpha({ length: { min: 0, max: 200 } }),
  type: faker.helpers.arrayElement(['Focus', 'Label', 'Lens']),
  color: faker.string.hexadecimal({ length: 6, prefix: '#' }),
  icon: faker.helpers.arrayElement([
    'book',
    'pie-chart',
    'calendar',
    'chat-bubbles',
    'line-chart',
    'dollar-sign',
    'customers',
    'compass',
    'lens'
  ]),
  start_at: faker.date.past(),
  end_at: faker.date.past(),
  embeddings: [...new Array(faker.number.int({ min: 1, max: 20 })).keys()].map(() =>
    faker.number.int()
  ),
  links: [...new Array(faker.number.int({ min: 1, max: 20 })).keys()].map(() => ({
    title: faker.string.alpha({ length: { min: 0, max: 30 } }),
    url: faker.internet.url()
  })),
  collaborators: [...new Array(faker.number.int({ min: 1, max: 20 })).keys()].map(() => ({
    id: faker.string.uuid(),
    name: faker.person.fullName()
  })),
  card_template: faker.string.alpha({ length: { min: 1, max: 5000 } }),
  formatting_instructions: faker.string.alpha({
    length: { min: 1, max: 5000 }
  }),
  understanding: faker.lorem.paragraph(),
  insight_config: {
    max_days: faker.number.int({ min: 2, max: 30 }),
    max_cards: faker.number.int({ min: 2, max: 30 }),
    duration_days: faker.number.int({ min: 1, max: 30 })
  },
  lens_config: {
    card_filter: {
      query: faker.lorem.words(),
      sub_sources: [...new Array(faker.number.int({ min: 1, max: 20 })).keys()].map(() =>
        faker.helpers.arrayElement([
          'Google_Meeting',
          'Teams_Meeting',
          'Webex_Meeting',
          'Zoom_Meeting',
          'Slack_Digest',
          'Teams_Digest',
          'Document_Upload',
          'Earnings_Call',
          'Email_Thread'
        ])
      ),
      participants_emails: [...new Array(faker.number.int({ min: 1, max: 200 })).keys()].map(() =>
        faker.lorem.words()
      ),
      participants_domains: [...new Array(faker.number.int({ min: 1, max: 200 })).keys()].map(() =>
        faker.lorem.words()
      ),
      initiative_ids: [...new Array(faker.number.int({ min: 1, max: 200 })).keys()].map(() =>
        faker.lorem.words()
      ),
      tags: [...new Array(faker.number.int({ min: 1, max: 200 })).keys()].map(() =>
        faker.lorem.words()
      ),
      label_ids: [...new Array(faker.number.int({ min: 1, max: 200 })).keys()].map(() =>
        faker.lorem.words()
      ),
      not_label_ids: [...new Array(faker.number.int({ min: 1, max: 200 })).keys()].map(() =>
        faker.lorem.words()
      )
    },
    include_earnings_call: faker.datatype.boolean(),
    match_prompt: faker.string.alpha({ length: { min: 1, max: 5000 } }),
    explanatory_prompt: faker.string.alpha({
      length: { min: 1, max: 5000 }
    }),
    high_priority_examples_prompt: faker.string.alpha({
      length: { min: 1, max: 5000 }
    }),
    min_similarity_score: faker.number.int(),
    min_topics_match: faker.number.int({ min: 1, max: 100 })
  },
  match_rules: {
    titles: [...new Array(faker.number.int({ min: 1, max: 20 })).keys()].map(() => ({
      value: faker.string.alpha({ length: { min: 1, max: 255 } }),
      min_occurrences: faker.number.int({ min: 1, max: 30 }),
      required: faker.datatype.boolean()
    })),
    domains: [...new Array(faker.number.int({ min: 1, max: 20 })).keys()].map(() => ({
      value: faker.string.alpha({ length: { min: 1, max: 255 } }),
      min_occurrences: faker.number.int({ min: 1, max: 30 }),
      required: faker.datatype.boolean()
    })),
    not_domains: [...new Array(faker.number.int({ min: 1, max: 20 })).keys()].map(() => ({
      value: faker.string.alpha({ length: { min: 1, max: 255 } }),
      min_occurrences: faker.number.int({ min: 1, max: 30 }),
      required: faker.datatype.boolean()
    })),
    only_domains: [...new Array(faker.number.int({ min: 1, max: 20 })).keys()].map(() =>
      faker.lorem.words()
    ),
    never_domains: [...new Array(faker.number.int({ min: 1, max: 20 })).keys()].map(() =>
      faker.lorem.words()
    ),
    emails: [...new Array(faker.number.int({ min: 1, max: 20 })).keys()].map(() => ({
      value: faker.string.alpha({ length: { min: 1, max: 255 } }),
      min_occurrences: faker.number.int({ min: 1, max: 30 }),
      required: faker.datatype.boolean()
    })),
    organizers: [...new Array(faker.number.int({ min: 1, max: 20 })).keys()].map(() => ({
      value: faker.string.alpha({ length: { min: 1, max: 255 } }),
      min_occurrences: faker.number.int({ min: 1, max: 30 }),
      required: faker.datatype.boolean()
    })),
    schedule: faker.datatype.boolean()
  },
  visibility: faker.helpers.arrayElement(['Collaborators', 'Owner', 'Tenant']),
  sensitive: faker.datatype.boolean(),
  tags: [...new Array(faker.number.int({ min: 1, max: 100 })).keys()].map(() =>
    faker.lorem.words()
  ),
  tenant: {
    id: faker.string.uuid(),
    name: faker.company.name()
  },
  creator: {
    id: faker.string.uuid(),
    name: faker.person.fullName()
  },
  archived_at: faker.date.past().getTime(),
  created_at: faker.date.past().getTime(),
  updated_at: faker.date.past().getTime()
};

const FAKE_BOT = {
  id: faker.string.uuid(),
  video_url: faker.internet.url(),
  media_retention_end: faker.date.past(),
  status_changes: [...new Array(faker.number.int({ min: 1, max: 20 })).keys()].map(() => ({
    code: faker.lorem.words(),
    sub_code: faker.lorem.words(),
    message: faker.lorem.words(),
    created_at: faker.date.past()
  })),
  meeting_metadata: {
    title: faker.lorem.words()
  },
  meeting_participants: [...new Array(faker.number.int({ min: 1, max: 20 })).keys()].map(() => ({
    id: faker.string.uuid(),
    name: faker.person.fullName(),
    events: [...new Array(faker.number.int({ min: 1, max: 20 })).keys()].map(() => ({
      code: faker.lorem.words(),
      sub_code: faker.lorem.words(),
      message: faker.lorem.words(),
      created_at: faker.date.past()
    }))
  })),
  calendar_meeting_id: faker.string.uuid(),
  calendar_meetings: [...new Array(faker.number.int({ min: 1, max: 20 })).keys()].map(() =>
    [...new Array(5).keys()]
      .map(() => ({ [faker.lorem.word()]: {} }))
      .reduce((acc, next) => Object.assign(acc, next), {})
  ),
  calendar_user_id: faker.string.uuid(),
  transcription_options: {
    provider: faker.helpers.arrayElement([
      'assembly_ai',
      'deepgram',
      'gladia',
      'gladia_v2',
      'rev',
      'upload'
    ]),
    assembly_ai: {
      word_boost: [...new Array(faker.number.int({ min: 1, max: 20 })).keys()].map(() =>
        faker.lorem.words()
      )
    },
    gladia: {
      language_behaviour: faker.helpers.arrayElement([
        'manual',
        'automatic single language',
        'automatic multiple languages'
      ]),
      language: faker.lorem.words(),
      transcription_hint: faker.lorem.words(),
      endpointing: faker.number.int(),
      model_type: faker.helpers.arrayElement(['fast', 'accurate']),
      audio_enhancer: faker.datatype.boolean()
    },
    gladia_v2: {
      code_switching: faker.datatype.boolean(),
      custom_vocabulary: faker.datatype.boolean(),
      custom_vocabulary_config: {
        vocabulary: [...new Array(faker.number.int({ min: 1, max: 20 })).keys()].map(() => ({
          value: faker.lorem.words()
        }))
      }
    },
    rev: {
      custom_vocabulary_id: faker.string.uuid()
    }
  },
  origin: faker.helpers.arrayElement(['Auto_By_Person', 'Auto_By_Tenant', 'Manual']),
  earnings_call: {
    symbol: faker.lorem.words(),
    company_name: faker.person.fullName(),
    domain: faker.lorem.words(),
    quarter: faker.number.int({ min: 1, max: 4 }),
    year: faker.number.int({ min: 2000, max: 2030 }),
    released_at: faker.date.past()
  },
  summary: faker.lorem.words(),
  summary_vector: [...new Array(faker.number.int({ min: 1, max: 20 })).keys()].map(() =>
    faker.number.int()
  ),
  summary_vector_0: [...new Array(faker.number.int({ min: 1, max: 20 })).keys()].map(() =>
    faker.number.int()
  ),
  summary_vector_1: [...new Array(faker.number.int({ min: 1, max: 20 })).keys()].map(() =>
    faker.number.int()
  ),
  num_of_status_changes: faker.number.int(),
  num_of_transcripts: faker.number.int(),
  outbound: {
    meeting_url: faker.internet.url(),
    bot_name: faker.person.fullName(),
    recording_mode: faker.helpers.arrayElement([
      'speaker_view',
      'gallery_view',
      'gallery_view_v2',
      'audio_only'
    ]),
    recording_mode_options: {
      participant_video_when_screenshare: faker.helpers.arrayElement(['hide', 'beside', 'overlap']),
      start_recording_on: faker.helpers.arrayElement([
        'call_join',
        'participant_join',
        'participant_speak',
        'manual'
      ])
    },
    real_time_transcription: {
      destination_url: faker.internet.url(),
      partial_results: faker.datatype.boolean()
    },
    real_time_media: {
      websocket_video_destination_url: faker.internet.url(),
      websocket_audio_destination_url: faker.internet.url()
    },
    transcription_options: {
      provider: faker.helpers.arrayElement([
        'assembly_ai',
        'deepgram',
        'gladia',
        'gladia_v2',
        'rev',
        'upload'
      ]),
      assembly_ai: {
        word_boost: [...new Array(faker.number.int({ min: 1, max: 20 })).keys()].map(() =>
          faker.lorem.words()
        )
      },
      gladia: {
        language_behaviour: faker.helpers.arrayElement([
          'manual',
          'automatic single language',
          'automatic multiple languages'
        ]),
        language: faker.lorem.words(),
        transcription_hint: faker.lorem.words(),
        endpointing: faker.number.int(),
        model_type: faker.helpers.arrayElement(['fast', 'accurate']),
        audio_enhancer: faker.datatype.boolean()
      },
      gladia_v2: {
        code_switching: faker.datatype.boolean(),
        custom_vocabulary: faker.datatype.boolean(),
        custom_vocabulary_config: {
          vocabulary: [...new Array(faker.number.int({ min: 1, max: 20 })).keys()].map(() => ({
            value: faker.lorem.words()
          }))
        }
      },
      rev: {
        custom_vocabulary_id: faker.string.uuid()
      }
    },
    intelligence: {
      assembly_ai: {
        redact_pii_policies: [...new Array(faker.number.int({ min: 1, max: 20 })).keys()].map(() =>
          faker.lorem.words()
        ),
        auto_highlights: faker.datatype.boolean(),
        content_safety: faker.datatype.boolean(),
        iab_categories: faker.datatype.boolean(),
        sentiment_analysis: faker.datatype.boolean(),
        auto_chapters: faker.datatype.boolean(),
        entity_detection: faker.datatype.boolean()
      }
    },
    chat: {
      on_bot_join: {
        send_to: faker.helpers.arrayElement(['everyone', 'everyone_except_host', 'host']),
        message: faker.lorem.words()
      },
      on_participant_join: {
        message: faker.lorem.words(),
        exclude_host: faker.datatype.boolean()
      }
    },
    google_meet: {
      google_login_group_id: faker.string.uuid(),
      login_required: faker.datatype.boolean()
    },
    zoom: {
      join_token_url: faker.internet.url(),
      user_email: faker.internet.email(),
      require_recording_permission: faker.datatype.boolean(),
      request_recording_permission_on_host_join: faker.datatype.boolean()
    },
    join_at: faker.date.past(),
    automatic_video_output: {
      in_call_recording: {
        kind: faker.helpers.arrayElement(['jpeg']),
        b64_data: faker.string.alpha({ length: { min: 128, max: 8192 } })
      },
      in_call_not_recording: {
        kind: faker.helpers.arrayElement(['jpeg']),
        b64_data: faker.string.alpha({ length: { min: 128, max: 8192 } })
      }
    }
  },
  request: {
    url: faker.internet.url(),
    name: faker.person.fullName(),
    provider: faker.helpers.arrayElement(['AzureAI', 'Bedrock', 'Gemini', 'OpenAI', 'PaLM']),
    model: faker.string.alpha({ length: { min: 1, max: 512 } }),
    async: faker.datatype.boolean(),
    notify: faker.datatype.boolean(),
    notifyMessage: faker.string.alpha({ length: { min: 1, max: 10000 } }),
    callbackUrl: faker.internet.url(),
    wordBoost: [...new Array(faker.number.int({ min: 1, max: 1000 })).keys()].map(() =>
      faker.lorem.words()
    ),
    maxLength: faker.number.int({ min: 1, max: 10000 }),
    prompt: faker.string.alpha({ length: { min: 1, max: 10000 } }),
    temperature: faker.number.int(),
    followupModel: faker.string.alpha({ length: { min: 1, max: 512 } }),
    followupMaxLength: faker.number.int({ min: 1, max: 10000 }),
    followupPrompt: faker.string.alpha({ length: { min: 1, max: 10000 } }),
    followupTemperature: faker.number.int(),
    recapModel: faker.string.alpha({ length: { min: 1, max: 512 } }),
    recapMaxLength: faker.number.int({ min: 1, max: 10000 }),
    recapPrompt: faker.string.alpha({ length: { min: 1, max: 10000 } }),
    recapPromptX: faker.string.alpha({ length: { min: 1, max: 10000 } }),
    recapTemperature: faker.number.int(),
    summaryIntervalDuration: faker.number.int(),
    titleModel: faker.string.alpha({ length: { min: 1, max: 512 } }),
    titleMaxLength: faker.number.int({ min: 1, max: 10000 }),
    titlePrompt: faker.string.alpha({ length: { min: 1, max: 10000 } }),
    titleTemperature: faker.number.int(),
    todoModel: faker.string.alpha({ length: { min: 1, max: 512 } }),
    todoMaxLength: faker.number.int({ min: 1, max: 10000 }),
    todoPrompt: faker.string.alpha({ length: { min: 1, max: 10000 } }),
    todoTemperature: faker.number.int(),
    topicPrompt: faker.string.alpha({ length: { min: 1, max: 10000 } }),
    useQRCode: faker.datatype.boolean(),
    inCallRecordingMediaUrl: faker.internet.url(),
    inCallNotRecordingMediaUrl: faker.internet.url()
  },
  transcripts: [...new Array(faker.number.int({ min: 1, max: 20 })).keys()].map(() => ({
    speaker: faker.lorem.words(),
    words: [...new Array(faker.number.int({ min: 1, max: 20 })).keys()].map(() => ({
      text: faker.lorem.words(),
      start_time: faker.number.int(),
      end_time: faker.number.int()
    })),
    is_final: faker.datatype.boolean(),
    start_time: faker.number.int(),
    end_time: faker.number.int(),
    duration: faker.number.int(),
    segment: faker.lorem.words(),
    summary: faker.lorem.words(),
    title: faker.lorem.words(),
    todos: faker.lorem.words(),
    todos_title: faker.lorem.words(),
    participant: [...new Array(5).keys()]
      .map(() => ({ [faker.lorem.word()]: {} }))
      .reduce((acc, next) => Object.assign(acc, next), {})
  })),
  followups: [...new Array(faker.number.int({ min: 5, max: 20 })).keys()].map(() => ({
    value: faker.lorem.sentence(),
    assignee: faker.person.fullName()
  })),
  speakers_: [...new Array(faker.number.int({ min: 1, max: 20 })).keys()].map(() => ({
    name: faker.person.fullName(),
    duration: faker.number.int(),
    behavior: faker.string.alpha({ length: { min: 1, max: 100 } }),
    demeanor: faker.helpers.arrayElement(['balanced', 'strategic', 'tactical'])
  })),
  topics: [...new Array(faker.number.int({ min: 1, max: 20 })).keys()].map(() => ({
    title: faker.string.alpha({ length: { min: 1, max: 512 } }),
    details: faker.string.alpha({ length: { min: 1, max: 5000 } }),
    speakers: [...new Array(faker.number.int({ min: 1, max: 100 })).keys()].map(() =>
      faker.lorem.words()
    ),
    sentiment: faker.number.int({ min: -1, max: 1 }),
    strategic_vs_tactical: faker.helpers.arrayElement(['strategic', 'tactical']),
    embeddings: [...new Array(faker.number.int({ min: 1, max: 20 })).keys()].map(() =>
      faker.number.int()
    )
  })),
  recap: {
    headline: faker.lorem.sentence(),
    sections: [...new Array(faker.number.int({ min: 3, max: 10 })).keys()].map(() => ({
      title: faker.lorem.sentence(),
      content: faker.lorem.paragraphs()
    }))
  },
  tweet: faker.lorem.words(),
  highlights: [...new Array(faker.number.int({ min: 1, max: 20 })).keys()].map(() => ({
    id: faker.string.uuid(),
    headline: faker.string.alpha({ length: { min: 1, max: 5000 } }),
    summary: faker.string.alpha({ length: { min: 1, max: 5000 } }),
    evidence: faker.string.alpha({ length: { min: 1, max: 5000 } }),
    start_time: faker.string.alpha({ length: { min: 1, max: 20 } }),
    end_time: faker.string.alpha({ length: { min: 1, max: 20 } })
  })),
  tenant: {
    id: faker.string.uuid(),
    name: faker.person.fullName()
  },
  creator: {
    id: faker.string.uuid(),
    name: faker.person.fullName()
  },
  created_at: faker.date.past(),
  meeting_url: {},
  join_at: faker.date.past(),
  updated_at: faker.date.past(),
  speakers: [...new Array(5).keys()]
    .map(() => ({ [faker.lorem.word()]: faker.number.int() }))
    .reduce((acc, next) => Object.assign(acc, next), {})
};

const FAKE_MEETING = {
  id: faker.string.uuid(),
  title: faker.lorem.words(),
  description: faker.string.alpha({ length: { min: 0, max: 65536 } }),
  location: faker.string.alpha({ length: { min: 0, max: 512 } }),
  url: faker.internet.url(),
  start_at: faker.date.past(),
  end_at: faker.date.past(),
  embeddings: [...new Array(faker.number.int({ min: 1, max: 20 })).keys()].map(() =>
    faker.number.int()
  ),
  status: {
    id: faker.string.uuid(),
    name: faker.person.fullName()
  },
  attendees: [...new Array(faker.number.int({ min: 1, max: 20 })).keys()].map(() => ({
    id: faker.string.uuid(),
    name: faker.person.fullName(),
    status: {
      id: faker.string.uuid(),
      name: faker.person.fullName()
    }
  })),
  bot_id: faker.string.uuid(),
  bot_attendee: faker.datatype.boolean(),
  calendar: {
    id: faker.string.uuid(),
    name: faker.person.fullName()
  },
  domains: [...new Array(faker.number.int({ min: 1, max: 100 })).keys()].map(() =>
    faker.lorem.words()
  ),
  initiative: {
    id: faker.string.uuid(),
    title: faker.string.alpha({ length: { min: 0, max: 512 } })
  },
  initiative_match: faker.helpers.arrayElement(['Auto', 'Manual', 'Rule']),
  linked: {
    id: faker.string.uuid(),
    title: faker.string.alpha({ length: { min: 0, max: 512 } }),
    bot_id: faker.string.uuid()
  },
  nylas: [...new Array(5).keys()]
    .map(() => ({ [faker.lorem.word()]: {} }))
    .reduce((acc, next) => Object.assign(acc, next), {}),
  parent_id: faker.string.uuid(),
  preparation: faker.string.alpha({ length: { min: 1, max: 5000 } }),
  preparation_headline: faker.string.alpha({ length: { min: 1, max: 512 } }),
  last_minute_prep: {
    name: faker.person.fullName(),
    target_channel: faker.helpers.arrayElement(['email', 'push', 'sms']),
    contents: {
      en: faker.string.alpha({ length: { min: 1, max: 1000 } })
    },
    headings: {
      en: faker.string.alpha({ length: { min: 1, max: 1000 } })
    },
    include_aliases: {
      external_id: [...new Array(faker.number.int({ min: 1, max: 20 })).keys()].map(() =>
        faker.lorem.words()
      )
    },
    idempotency_key: faker.string.alpha({ length: { min: 1, max: 255 } }),
    data: [...new Array(5).keys()]
      .map(() => ({ [faker.lorem.word()]: {} }))
      .reduce((acc, next) => Object.assign(acc, next), {}),
    send_after: faker.date.past()
  },
  shares: [...new Array(faker.number.int({ min: 1, max: 20 })).keys()].map(() => ({
    email: faker.internet.email(),
    created_at: faker.date.past()
  })),
  watchers: [...new Array(faker.number.int({ min: 1, max: 20 })).keys()].map(() => ({
    id: faker.string.uuid(),
    name: faker.person.fullName()
  })),
  tags: [...new Array(faker.number.int({ min: 1, max: 100 })).keys()].map(() =>
    faker.lorem.words()
  ),
  labels: [...new Array(faker.number.int({ min: 1, max: 20 })).keys()].map(() => ({
    id: faker.string.uuid(),
    title: faker.string.alpha({ length: { min: 0, max: 512 } }),
    match: faker.helpers.arrayElement(['Auto', 'Manual', 'Rule'])
  })),
  attachments: [...new Array(faker.number.int({ min: 1, max: 20 })).keys()].map(() => ({
    key: faker.string.alpha({ length: { min: 1, max: 500 } }),
    name: faker.person.fullName(),
    type: faker.string.alpha({ length: { min: 1, max: 255 } }),
    creator: {
      id: faker.string.uuid(),
      name: faker.person.fullName()
    },
    created_at: faker.date.past()
  })),
  sensitive: faker.datatype.boolean(),
  tenant: {
    id: faker.string.uuid(),
    name: faker.person.fullName()
  },
  creator: {
    id: faker.string.uuid(),
    name: faker.person.fullName()
  },
  archived_at: faker.date.past().getTime(),
  created_at: faker.date.past().getTime(),
  updated_at: faker.date.past().getTime()
};

const FAKE_FULL_CARDS = FAKE_INSIGHT.cards
  .map((card) => ({
    id: card.id,
    headline: faker.lorem.sentence(),
    sections: [...new Array(faker.number.int({ min: 1, max: 20 })).keys()].map(() => ({
      title: faker.string.alpha({ length: { min: 0, max: 512 } }),
      content: faker.string.alpha({ length: { min: 0, max: 65536 } })
    })),
    initiative: {
      id: faker.string.uuid(),
      title: faker.string.alpha({ length: { min: 0, max: 512 } }),
      type: faker.helpers.arrayElement(['Focus', 'Label', 'Lens'])
    },
    tenant: {
      id: faker.string.uuid(),
      name: faker.person.fullName()
    },
    person: {
      id: faker.string.uuid(),
      name: faker.person.fullName()
    },
    source: faker.helpers.arrayElement(['Bot', 'Channel', 'Meeting']),
    sub_source: faker.helpers.arrayElement([
      'Google_Meeting',
      'Teams_Meeting',
      'Webex_Meeting',
      'Zoom_Meeting',
      'Slack_Digest',
      'Teams_Digest',
      'Document_Upload',
      'Earnings_Call',
      'Email_Thread'
    ]),
    source_data: {
      id: faker.string.uuid(),
      title: faker.lorem.words()
    },
    batch_id: faker.string.uuid(),
    participants: [...new Array(faker.number.int({ min: 1, max: 100 })).keys()].map(() => ({
      id: faker.string.uuid(),
      name: faker.person.fullName(),
      email: faker.internet.email(),
      title: faker.string.alpha({ length: { min: 0, max: 500 } }),
      company_name: faker.person.fullName(),
      company_type: faker.string.alpha({ length: { min: 0, max: 255 } }),
      company_industry: faker.string.alpha({ length: { min: 0, max: 255 } }),
      avatar_url: faker.internet.url(),
      comment: faker.string.alpha({ length: { min: 0, max: 500 } }),
      status: faker.helpers.arrayElement(['yes', 'no', 'maybe', 'noreply'])
    })),
    supporting_data: [...new Array(faker.number.int({ min: 1, max: 20 })).keys()].map(() => ({
      value: faker.string.alpha({ length: { min: 1, max: 5000 } }),
      data: [...new Array(faker.number.int({ min: 1, max: 20 })).keys()].map(() =>
        faker.number.int()
      )
    })),
    num_of_supporting_data: faker.number.int(),
    tags: [...new Array(faker.number.int({ min: 1, max: 100 })).keys()].map(() =>
      faker.lorem.words()
    ),
    labels: [...new Array(faker.number.int({ min: 1, max: 20 })).keys()].map(() => ({
      id: faker.string.uuid(),
      title: faker.string.alpha({ length: { min: 0, max: 512 } }),
      match: faker.helpers.arrayElement(['Auto', 'Manual', 'Rule'])
    })),
    comments: faker.string.alpha({ length: { min: 1, max: 5000 } }),
    high_priority: faker.datatype.boolean(),
    high_priority_explanation: faker.string.alpha({
      length: { min: 1, max: 5000 }
    }),
    shares: [...new Array(faker.number.int({ min: 1, max: 20 })).keys()].map(() => ({
      email: faker.internet.email(),
      created_at: faker.date.past()
    })),
    preferred: faker.datatype.boolean(),
    archived: faker.datatype.boolean(),
    read_at: faker.date.past(),
    shared_at: faker.date.past(),
    created_at: faker.date.past(),
    updated_at: faker.date.past()
  }))
  .reduce((acc, obj) => {
    acc[obj.id] = obj;
    return acc;
  }, {});

const FAKE_USER_NAME = faker.person.fullName();
const FAKE_TENANT_NAME = faker.company.name();

function generateContentWithSources() {
  const sentences = faker.lorem.sentences(faker.number.int({ min: 3, max: 8 })).split('. ');

  return sentences
    .map((sentence) => {
      // 30% chance to add a source after each sentence
      if (Math.random() < 0.3 && sentence.length > 10) {
        const sourceNum = faker.number.int({ min: 1, max: 5 });
        return `${sentence} \`${sourceNum}\``;
      }
      return sentence;
    })
    .join('. ');
}

export default function PDFSection() {
  const pdfHeight = useBreakpointValue({ base: 1000, xl: 600 });
  const { topicGroups } = useTopicGroupsAndSummaries(FAKE_MEETING, FAKE_BOT);
  const [pdfType, setPDFType] = useState('meeting');

  const pdfMap = {
    meeting: (
      <MeetingPDF
        userName={FAKE_USER_NAME}
        bot={FAKE_BOT}
        meeting={FAKE_MEETING}
        tenantName={FAKE_TENANT_NAME}
        topicGroups={topicGroups}
      />
    ),
    insight: (
      <InsightPDF
        insight={FAKE_INSIGHT}
        initiative={FAKE_INITIATIVE}
        fullCards={FAKE_FULL_CARDS}
        userName={FAKE_USER_NAME}
        tenantName={FAKE_TENANT_NAME}
      />
    )
  };

  return (
    <Stack gap={8}>
      <RadioGroup
        onChange={setPDFType}
        value={pdfType}
      >
        <Stack
          direction='row'
          gap={8}
        >
          {['meeting', 'insight'].map((v) => (
            <Radio
              key={v}
              value={v}
            >
              {capitalize(v)}
            </Radio>
          ))}
        </Stack>
      </RadioGroup>
      <PDFViewer
        width='100%'
        height={pdfHeight}
      >
        {pdfMap[pdfType]}
      </PDFViewer>
    </Stack>
  );
}
